import { useEffect, useState } from 'react';

export interface ReasoningStep {
  phase: string;
  label: string;
}

export interface ReasoningLoopRound {
  round: number;
  thinking?: string;
  thinkingStreaming?: boolean;
  exploring?: string;
  exploringStreaming?: boolean;
  toolLayer?: 'L1' | 'L2';
  plan?: string;
  tool?: string;
  observe?: string;
  sql?: string;
  critic?: string;
  planStreaming?: boolean;
  toolStreaming?: boolean;
  observeStreaming?: boolean;
  sqlStreaming?: boolean;
  criticStreaming?: boolean;
}

export interface MessageReasoning {
  plan: string;
  tool: string;
  observe: string;
  sql: string;
  critic: string;
  summary: string;
  thinking?: string;
  thinkingStreaming?: boolean;
  exploring?: string;
  exploringStreaming?: boolean;
  planStreaming?: boolean;
  toolStreaming?: boolean;
  observeStreaming?: boolean;
  sqlStreaming?: boolean;
  criticStreaming?: boolean;
  summaryStreaming?: boolean;
  summaryLine?: string;
  summarySteps?: ReasoningStep[];
  elapsedMs?: number;
  loopRounds?: ReasoningLoopRound[];
}

interface ReasoningPanelProps {
  reasoning: MessageReasoning;
  expanded: boolean;
  onToggle: () => void;
  streaming?: boolean;
  streamingLabel?: string;
  showDetail?: boolean;
  /** Prefer server latency_ms when present */
  latencyMs?: number;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    llm_calls?: number;
  };
}

function formatElapsed(ms?: number): string {
  if (!ms || ms <= 0) return '';
  return `${(ms / 1000).toFixed(1).replace(/\.0$/, '')}s`;
}

function formatTokenCount(n: number): string {
  if (n >= 10000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

function formatTurnMeta(
  latencyMs?: number,
  usage?: ReasoningPanelProps['usage']
): { label: string; title?: string } {
  const parts: string[] = [];
  const elapsed = formatElapsed(latencyMs);
  if (elapsed) parts.push(elapsed);
  const total = usage
    ? usage.total_tokens || usage.prompt_tokens + usage.completion_tokens
    : 0;
  if (total > 0) parts.push(`${formatTokenCount(total)} tok`);
  if (!parts.length) return { label: '' };
  const title =
    total > 0
      ? [
          `prompt ${usage!.prompt_tokens}`,
          `completion ${usage!.completion_tokens}`,
          usage?.llm_calls ? `calls ${usage.llm_calls}` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : undefined;
  return { label: parts.join(' · '), title };
}

function mergeExploring(round: ReasoningLoopRound): string {
  const parts = [round.exploring, round.tool, round.observe, round.plan]
    .filter(Boolean)
    .join('');
  return parts.replace(/^\n+/, '');
}

function mergeThinking(round: ReasoningLoopRound): string {
  const fromPlan = round.plan && !round.exploring ? round.plan : '';
  return (round.thinking || fromPlan || '').replace(/^\n+/, '');
}

function hasRoundContent(round: ReasoningLoopRound): boolean {
  return Boolean(
    mergeExploring(round) ||
      mergeThinking(round) ||
      round.sql ||
      round.critic ||
      round.planStreaming ||
      round.toolStreaming ||
      round.observeStreaming ||
      round.sqlStreaming ||
      round.thinkingStreaming ||
      round.exploringStreaming
  );
}

function hasDetailContent(reasoning: MessageReasoning): boolean {
  if (reasoning.thinking || reasoning.exploring || reasoning.plan || reasoning.tool || reasoning.summary) {
    return true;
  }
  if (reasoning.loopRounds?.some(hasRoundContent)) return true;
  return Boolean(reasoning.observe || reasoning.sql || reasoning.critic);
}

function isStreaming(reasoning: MessageReasoning): boolean {
  if (
    reasoning.planStreaming ||
    reasoning.toolStreaming ||
    reasoning.summaryStreaming ||
    reasoning.observeStreaming ||
    reasoning.sqlStreaming ||
    reasoning.criticStreaming ||
    reasoning.thinkingStreaming ||
    reasoning.exploringStreaming
  ) {
    return true;
  }
  return Boolean(
    reasoning.loopRounds?.some(
      (r) =>
        r.planStreaming ||
        r.toolStreaming ||
        r.observeStreaming ||
        r.sqlStreaming ||
        r.criticStreaming ||
        r.thinkingStreaming ||
        r.exploringStreaming
    )
  );
}

function LayerBadge({ layer }: { layer: 'L1' | 'L2' }) {
  return (
    <span className={`reasoning-layer-badge reasoning-layer-badge--${layer.toLowerCase()}`} title={layer === 'L1' ? '业务检索工具' : '数据探索工具'}>
      {layer}
    </span>
  );
}

function CursorSection({
  title,
  subtitle,
  text,
  streaming,
  defaultOpen = true,
}: {
  title: string;
  subtitle?: string;
  text: string;
  streaming?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (streaming) setOpen(true);
  }, [streaming]);

  if (!text && !streaming) return null;

  return (
    <div className={`reasoning-cursor-section${streaming ? ' reasoning-cursor-section--live' : ''}`}>
      <button
        type="button"
        className="reasoning-cursor-section-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="reasoning-cursor-chevron" aria-hidden>
          {open ? '▾' : '▸'}
        </span>
        <span className="reasoning-cursor-section-title">{title}</span>
        {subtitle && <span className="reasoning-cursor-section-sub">{subtitle}</span>}
        {streaming && <span className="reasoning-live">streaming</span>}
      </button>
      {open && (
        <pre className="reasoning-cursor-section-body">
          {text}
          {streaming && <span className="reasoning-cursor">▍</span>}
        </pre>
      )}
    </div>
  );
}

function PhaseBlock({
  label,
  text,
  streaming,
}: {
  label: string;
  text: string;
  streaming?: boolean;
}) {
  if (!text && !streaming) return null;
  const displayText = text.replace(/^\n+/, '');
  if (!displayText && !streaming) return null;
  return (
    <div className="reasoning-phase">
      <div className="reasoning-phase-label">
        {label}
        {streaming && <span className="reasoning-live">思考中</span>}
      </div>
      <pre className="reasoning-text">
        {displayText}
        {streaming && <span className="reasoning-cursor">▍</span>}
      </pre>
    </div>
  );
}

function ReasoningTimeline({ rounds }: { rounds: ReasoningLoopRound[] }) {
  const sorted = [...rounds].filter(hasRoundContent).sort((a, b) => a.round - b.round);
  if (sorted.length === 0) return null;

  return (
    <div className="reasoning-timeline">
      {sorted.map((round, idx) => {
        const isLast = idx === sorted.length - 1;
        const exploring = mergeExploring(round);
        const thinking = mergeThinking(round);
        const live =
          round.thinkingStreaming ||
          round.exploringStreaming ||
          round.planStreaming ||
          round.toolStreaming ||
          round.observeStreaming ||
          round.sqlStreaming;
        return (
          <div
            key={round.round}
            className={`reasoning-timeline-round${live ? ' reasoning-timeline-round--live' : ''}`}
          >
            <div className="reasoning-timeline-rail">
              <span className={`reasoning-timeline-dot${live ? ' reasoning-timeline-dot--live' : ''}`} />
              {!isLast && <span className="reasoning-timeline-line" />}
            </div>
            <div className="reasoning-timeline-content">
              <div className="reasoning-timeline-round-title">
                <span>第 {round.round} 轮</span>
                {round.toolLayer && <LayerBadge layer={round.toolLayer} />}
              </div>
              <CursorSection
                title="Exploring"
                subtitle="工具与观察"
                text={exploring}
                streaming={round.exploringStreaming || round.toolStreaming || round.observeStreaming}
              />
              <CursorSection
                title="Thinking"
                subtitle="模型推理"
                text={thinking}
                streaming={round.thinkingStreaming || round.planStreaming}
              />
              {round.sql && (
                <PhaseBlock label="SQL" text={round.sql} streaming={round.sqlStreaming} />
              )}
              {round.critic && (
                <PhaseBlock label="评审" text={round.critic} streaming={round.criticStreaming} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function deriveSummaryFallback(reasoning: MessageReasoning, steps: ReasoningStep[]): string {
  if (steps.length > 0) {
    return steps.map((s) => s.label).join(' · ');
  }
  const rounds = reasoning.loopRounds ?? [];
  for (let i = rounds.length - 1; i >= 0; i--) {
    const exploring = mergeExploring(rounds[i]);
    const toolLine = exploring.split('\n').find((l) => l.trim().startsWith('→'));
    if (toolLine) return toolLine.replace(/^→\s*/, '准备调用 ').trim();
  }
  const planLine = reasoning.plan
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l && !/^第 \d+ 步/.test(l) && !l.includes('分析中') && !l.includes('选择工具'));
  return planLine || '';
}

export function ReasoningPanel({
  reasoning,
  expanded,
  onToggle,
  streaming,
  streamingLabel,
  showDetail = true,
  latencyMs,
  usage,
}: ReasoningPanelProps) {
  const live = streaming || isStreaming(reasoning);
  const steps = reasoning.summarySteps ?? [];
  const detail = hasDetailContent(reasoning);
  const loopRounds = reasoning.loopRounds ?? [];
  const summaryText =
    reasoning.summaryLine ||
    deriveSummaryFallback(reasoning, steps) ||
    (live ? streamingLabel || '正在生成回复…' : detail ? '推理完成' : '');

  const turnMeta = formatTurnMeta(
    latencyMs && latencyMs > 0 ? latencyMs : reasoning.elapsedMs,
    usage
  );
  if (!summaryText && !detail && !live && !turnMeta.label) return null;

  const hasTimeline = loopRounds.some(hasRoundContent);
  const showBody = expanded || live;
  const topExploring = reasoning.exploring || '';
  const topThinking = reasoning.thinking || reasoning.plan || '';

  return (
    <div className={`reasoning-panel${expanded ? ' expanded' : ''}${live ? ' reasoning-panel--live' : ''}`}>
      <button type="button" className="reasoning-summary-bar" onClick={onToggle} aria-expanded={expanded}>
        <span className="reasoning-toggle-icon" aria-hidden>
          {expanded ? '▼' : '▶'}
        </span>
        <span
          className={`reasoning-summary-text${live ? ' reasoning-summary-text--live' : ''}`}
          aria-live={live ? 'polite' : undefined}
          aria-atomic="true"
        >
          {summaryText}
        </span>
        {turnMeta.label && !live && (
          <span className="reasoning-elapsed" title={turnMeta.title}>
            {turnMeta.label}
          </span>
        )}
        {live && <span className="reasoning-badge">进行中</span>}
      </button>
      {showBody && (
        <div className="reasoning-body">
          {steps.length > 0 && (
            <div className="reasoning-stepper">
              {steps.map((step) => (
                <div key={`${step.phase}-${step.label}`} className="reasoning-step">
                  <span className="reasoning-step-check">✓</span>
                  <span className="reasoning-step-phase">{step.phase}</span>
                  <span className="reasoning-step-label">{step.label}</span>
                </div>
              ))}
            </div>
          )}
          {detail && showDetail && (
            <div className="reasoning-detail">
              {(topExploring || topThinking) && (
                <div className="reasoning-cursor-root">
                  <CursorSection
                    title="Exploring"
                    subtitle="工具与观察"
                    text={topExploring}
                    streaming={reasoning.exploringStreaming}
                  />
                  <CursorSection
                    title="Thinking"
                    subtitle="模型推理"
                    text={topThinking}
                    streaming={reasoning.thinkingStreaming || reasoning.planStreaming}
                  />
                </div>
              )}
              {hasTimeline ? (
                <ReasoningTimeline rounds={loopRounds} />
              ) : (
                <>
                  {reasoning.tool && (
                    <PhaseBlock label="查询" text={reasoning.tool} streaming={reasoning.toolStreaming} />
                  )}
                  <PhaseBlock label="观察" text={reasoning.observe} streaming={reasoning.observeStreaming} />
                  <PhaseBlock label="SQL" text={reasoning.sql} streaming={reasoning.sqlStreaming} />
                  <PhaseBlock label="评审" text={reasoning.critic} streaming={reasoning.criticStreaming} />
                </>
              )}
              {hasTimeline && reasoning.sql && !loopRounds.some((r) => r.sql) && (
                <PhaseBlock label="SQL" text={reasoning.sql} streaming={reasoning.sqlStreaming} />
              )}
              <PhaseBlock label="总结" text={reasoning.summary} streaming={reasoning.summaryStreaming} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
